/**
 * My Tasks — DESIGN.md §7.5. The home screen.
 *
 * "Built-in cross-project view of open cards where `assignee = me`, grouped
 * **Overdue / Today / This week / Later / No date**, complete-toggles inline.
 * … It is a normal ViewConfig under the hood."
 *
 * That last sentence is the design: this screen builds a ViewConfig with an
 * `assignee = me` filter and hands it to the same engine every other surface
 * uses (§7.1). The three tabs and snooze arrive in M3; the grouping is here
 * now because a home screen that is one flat list is not the home screen §7.5
 * describes.
 */
import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { toast } from 'sonner';
import { Check, CheckSquare, Clock } from 'lucide-react';
import { defaultViewConfig, type TaskSummary } from '@slate/shared';
import { Page } from '@/components/page';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import { CardPanel } from '@/components/cards/card-panel';
import { CardAside, usePeekPresence } from '@/components/cards/card-aside';
import {
  BlockedBadge,
  CardCounts,
  DueDate,
  PriorityChip,
  StatusChip,
  TagChip,
} from '@/components/cards/chips';
import {
  listRows,
  useCompleteTask,
  useSnooze,
  useTaskByNumber,
  useTaskList,
} from '@/features/tasks/api';
import { useCurrentUser } from '@/lib/auth';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  card: z.coerce.number().int().positive().optional(),
  /**
   * #23 — the peek opens with the caret already in the description. Set by
   * the create flow and cleared when the peek closes, so a reload or a link
   * someone pastes never steals focus.
   */
  focus: z.literal('description').optional(),
});

export const Route = createFileRoute('/')({
  validateSearch: searchSchema,
  component: MyTasksPage,
});

/**
 * §7.5 — the five buckets, in the order a day is worked, plus the snoozed
 * bucket that sits below them all until its date arrives (R42).
 */
const GROUPS = ['Overdue', 'Today', 'This week', 'Later', 'No date', 'Snoozed'] as const;
type GroupName = (typeof GROUPS)[number];

function MyTasksPage(): JSX.Element {
  const me = useCurrentUser();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const complete = useCompleteTask();
  const [tab, setTab] = useState<'assigned' | 'collaborating' | 'completed'>('assigned');
  const snooze = useSnooze();

  const config = useMemo(
    () =>
      defaultViewConfig({
        // `me` is resolved server-side per request, so a saved variant of this
        // view stays personal for whoever opens it (§7.1).
        filters:
          tab === 'completed'
            ? [
                // §7.5 "Recently completed" — cards I was assigned that are done.
                // (Previously a copy of Collaborating with showCompleted false,
                // so it could never show a completed card — DAILY-1.)
                { field: 'assignee', op: 'eq', value: 'me' },
                { field: 'completed', op: 'eq', value: true },
              ]
            : [{ field: tab === 'assigned' ? 'assignee' : 'collaborator', op: 'eq', value: 'me' }],
        sort:
          tab === 'assigned'
            ? [
                { field: 'dueDate', dir: 'asc' },
                { field: 'priority', dir: 'desc' },
              ]
            : tab === 'completed'
              ? [{ field: 'completedAt', dir: 'desc' }]
              : [{ field: 'updatedAt', dir: 'desc' }],
        showCompleted: tab === 'completed',
      }),
    [tab],
  );

  const query = useTaskList(config);
  const { items, total } = listRows(query.data);

  const grouped = useMemo(() => groupByDue(items, me.timezone), [items, me.timezone]);

  /** ‹ › in the peek follow the order on screen: the groups, top to bottom. */
  const numbers = useMemo(
    () => GROUPS.flatMap((group) => grouped[group].map((task) => task.number)),
    [grouped],
  );
  const cursor = search.card === undefined ? -1 : numbers.indexOf(search.card);
  /** #44 — the peek's number outlives its URL by the length of the slide out. */
  const peek = usePeekPresence(search.card);
  const neighbours = {
    prev: cursor > 0 ? (numbers[cursor - 1] ?? null) : null,
    next: cursor >= 0 ? (numbers[cursor + 1] ?? null) : null,
  };

  const openCard = (number: number | undefined): void => {
    // #23's `focus` is deliberately dropped here: it belongs to the card that
    // was just created, not to the next one arrowed to, nor to a reopened peek.
    void navigate({ search: () => (number ? { card: number } : {}) });
  };

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] lg:h-dvh">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <Page
          title={`Good day, ${me.name.split(' ')[0] ?? 'there'}`}
          description={
            tab === 'assigned'
              ? 'Open cards assigned to you, grouped by when they are due.'
              : 'Cards you follow, most recently active first.'
          }
        >
          <div className="mb-4 flex items-center gap-1" role="tablist" aria-label="My Tasks tabs">
            {(
              [
                ['assigned', 'Assigned'],
                ['collaborating', 'Collaborating'],
                ['completed', 'Recently completed'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                onClick={() => setTab(value)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === value
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {query.isLoading ? (
            <LoadingRegion label="Loading your cards" />
          ) : total === 0 ? (
            <EmptyState
              icon={<CheckSquare className="size-8" />}
              title={
                tab === 'assigned' ? 'Nothing assigned to you' : 'You are not following any cards'
              }
              description={
                tab === 'assigned'
                  ? 'Cards appear here the moment someone assigns one to you. Until then, this is a good sign.'
                  : 'You follow a card by creating it, commenting on it, being mentioned, or adding yourself as a collaborator.'
              }
            />
          ) : tab !== 'assigned' ? (
            <ul className="rounded-lg border">
              {items.map((task) => (
                <MyTaskRow
                  key={task.id}
                  task={task}
                  selected={task.number === search.card}
                  dense={Boolean(search.card)}
                  onOpen={() => openCard(task.number)}
                  onToggle={() => toggleComplete(task, complete)}
                  onSnooze={undefined}
                />
              ))}
            </ul>
          ) : (
            <div className="space-y-5">
              {GROUPS.map((group) => {
                const rows = grouped[group];
                if (rows.length === 0) return null;
                return (
                  <section key={group}>
                    <h2
                      className={cn(
                        'mb-1.5 text-xs font-semibold uppercase tracking-wide',
                        group === 'Overdue'
                          ? 'text-red-600 dark:text-red-400'
                          : group === 'Today'
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-muted-foreground',
                      )}
                    >
                      {group} · {rows.length}
                      {group === 'Snoozed' ? (
                        <span className="ml-1.5 font-normal normal-case">
                          — hidden until their date; due reminders still fire
                        </span>
                      ) : null}
                    </h2>
                    <ul className="rounded-lg border">
                      {rows.map((task) => (
                        <MyTaskRow
                          key={task.id}
                          task={task}
                          selected={task.number === search.card}
                          dense={Boolean(search.card)}
                          onOpen={() => openCard(task.number)}
                          onToggle={() => toggleComplete(task, complete)}
                          onSnooze={(until) =>
                            until
                              ? snooze.snooze.mutate({ taskId: task.id, until })
                              : snooze.unsnooze.mutate(task.id)
                          }
                        />
                      ))}
                    </ul>
                  </section>
                );
              })}
              {query.hasNextPage ? (
                <Button
                  variant="secondary"
                  onClick={() => void query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  Load more
                </Button>
              ) : null}
            </div>
          )}
        </Page>
      </div>

      {/* #44 — `peek` holds the card number through the closing slide. */}
      {peek.number !== undefined ? (
        <CardAside open={peek.open}>
          <PeekPanel
            number={peek.number}
            focusDescription={search.focus === 'description'}
            onClose={() => openCard(undefined)}
            neighbours={neighbours}
            onNavigate={openCard}
          />
        </CardAside>
      ) : null}
    </div>
  );
}

function PeekPanel({
  number,
  focusDescription,
  onClose,
  neighbours,
  onNavigate,
}: {
  number: number;
  focusDescription: boolean;
  onClose: () => void;
  neighbours: { prev: number | null; next: number | null };
  onNavigate: (number: number) => void;
}): JSX.Element {
  const { data, isLoading } = useTaskByNumber(number);
  if (isLoading || !data) return <LoadingRegion label={`Loading #${number}`} />;
  return (
    <CardPanel
      taskId={data.id}
      focusDescription={focusDescription}
      onClose={onClose}
      neighbours={neighbours}
      onNavigate={onNavigate}
    />
  );
}

/**
 * Complete / reopen from My Work with the same §6.5 blocked guard every other
 * surface honours (this used to pass `force: true` — DAILY-3) and the §7.6
 * Undo the row's disappearance otherwise leaves you without (DAILY-4).
 */
function toggleComplete(task: TaskSummary, complete: ReturnType<typeof useCompleteTask>): void {
  const completing = !task.completedAt;
  const run = (force: boolean): void =>
    complete.mutate(
      { taskId: task.id, completed: completing, force },
      {
        onSuccess: () => {
          toast.success(completing ? `Completed #${task.number}` : `Reopened #${task.number}`, {
            duration: 8_000,
            action: {
              label: 'Undo',
              onClick: () =>
                complete.mutate({ taskId: task.id, completed: !completing, force: true }),
            },
          });
        },
        onError: (error) => {
          const details = (
            error as { details?: { reason?: string; blockers?: { number: number }[] } }
          ).details;
          if (details?.reason !== 'blocked') return;
          const blockers = (details.blockers ?? []).map((b) => `#${b.number}`).join(', ');
          if (window.confirm(`This card is blocked by ${blockers} — complete anyway?`)) run(true);
        },
      },
    );
  run(false);
}

function MyTaskRow({
  task,
  selected,
  dense,
  onOpen,
  onToggle,
  onSnooze,
}: {
  task: TaskSummary;
  selected: boolean;
  /** The peek has narrowed the list — drop to essentials (§13.6). */
  dense: boolean;
  onOpen: () => void;
  onToggle: () => void;
  /**
   * R42 — pass `null` to wake a card. Omitted on tabs where snooze makes no
   * sense (nothing to defer about a card you have already finished).
   */
  onSnooze?: ((until: string | null) => void) | undefined;
}): JSX.Element {
  const completed = Boolean(task.completedAt);
  return (
    <li
      className={cn(
        'hover:bg-accent/50 group flex items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0',
        selected && 'bg-accent',
      )}
      data-testid="my-task-row"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={completed ? `Reopen #${task.number}` : `Complete #${task.number}`}
        aria-pressed={completed}
        className={cn(
          'grid size-5 shrink-0 place-items-center rounded-full border transition-colors',
          completed
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'text-transparent hover:border-emerald-500 hover:text-emerald-500',
        )}
      >
        <Check className="size-3" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onOpen}
        // j/k in global-hotkeys.tsx walk these; Enter opens natively.
        data-task-row
        data-task-open
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <span className="text-muted-foreground w-12 shrink-0 font-mono text-xs">
          #{task.number}
        </span>
        <span
          className={cn(
            // A floor as well as `truncate`: without it a long title competing
            // with a badge shrinks to zero and the row shows chips only.
            'min-w-[5rem] flex-1 truncate',
            completed && 'text-muted-foreground line-through',
          )}
        >
          {task.title}
        </span>
        {task.isBlocked && !completed ? <BlockedBadge compact={dense} /> : null}
        {dense ? null : (
          <>
            {task.tags.slice(0, 2).map((tag) => (
              <TagChip key={tag.id} tag={tag} />
            ))}
            <CardCounts
              commentCount={task.commentCount}
              attachmentCount={task.attachmentCount}
              subtaskCount={task.subtaskCount}
              subtaskDoneCount={task.subtaskDoneCount}
            />
          </>
        )}
      </button>
      {onSnooze ? <SnoozeButton snoozedUntil={task.snoozedUntil} onSnooze={onSnooze} /> : null}
      <div
        className={cn('shrink-0 items-center gap-2', dense ? 'hidden xl:flex' : 'hidden sm:flex')}
      >
        {dense ? null : (
          <>
            {task.projects[0] ? (
              <span className="text-muted-foreground shrink-0 text-xs">
                {task.projects[0].project.key}
              </span>
            ) : null}
            <PriorityChip priority={task.priority} />
            <DueDate value={task.dueDate} completed={completed} />
          </>
        )}
        <StatusChip status={task.status} />
      </div>
    </li>
  );
}

/**
 * Snooze — R42, §7.5.
 *
 * Deliberately narrow, and the tooltip says so: it moves the card out of *this*
 * list until a date and changes nothing else. "Due-date notifications ignore
 * snooze — hiding a card is not permission to miss it."
 */
function SnoozeButton({
  snoozedUntil,
  onSnooze,
}: {
  snoozedUntil: string | null;
  onSnooze: (until: string | null) => void;
}): JSX.Element {
  if (snoozedUntil) {
    return (
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground shrink-0 text-xs"
        title={`Snoozed until ${snoozedUntil} — click to wake it now`}
        aria-label={`Wake card, snoozed until ${snoozedUntil}`}
        onClick={() => onSnooze(null)}
      >
        <Clock className="size-3.5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      className="text-muted-foreground hover:text-foreground shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
      title="Snooze — hides it from My Tasks until a date. Due-date reminders still fire."
      aria-label="Snooze this card"
      onClick={() => {
        const answer = window.prompt('Snooze until which date? (YYYY-MM-DD)', tomorrow());
        if (!answer) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(answer.trim())) {
          toast.error('Enter a date as YYYY-MM-DD');
          return;
        }
        onSnooze(answer.trim());
      }}
    >
      <Clock className="size-3.5" aria-hidden="true" />
    </button>
  );
}

function tomorrow(): string {
  return new Date(Date.now() + 86_400_000).toLocaleDateString('en-CA');
}

/**
 * Bucket by due date in the viewer's timezone (D10). "This week" runs to the
 * end of the current week rather than "within seven days" — a Friday card is
 * this week's problem, and a Monday card is not.
 */
function groupByDue(tasks: TaskSummary[], timezone: string): Record<GroupName, TaskSummary[]> {
  const out: Record<GroupName, TaskSummary[]> = {
    Overdue: [],
    Today: [],
    'This week': [],
    Later: [],
    'No date': [],
    Snoozed: [],
  };

  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  const todayDate = new Date(`${today}T00:00:00Z`);
  const weekday = (todayDate.getUTCDay() + 6) % 7; // Monday = 0
  const endOfWeek = new Date(todayDate.getTime() + (6 - weekday) * 86_400_000)
    .toISOString()
    .slice(0, 10);

  for (const task of tasks) {
    // A snooze that has passed is over: the card rejoins its due-date group
    // on the date, with no sweep required (§7.5).
    if (task.snoozedUntil && task.snoozedUntil > today) {
      out.Snoozed.push(task);
      continue;
    }
    if (!task.dueDate) out['No date'].push(task);
    else if (task.dueDate < today) out.Overdue.push(task);
    else if (task.dueDate === today) out.Today.push(task);
    else if (task.dueDate <= endOfWeek) out['This week'].push(task);
    else out.Later.push(task);
  }
  return out;
}
